import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, X } from "lucide-react";
import { submitArticleThunk } from "../../redux/news/newsActionThunks";
import { resetSubmitStatus } from "../../redux/news/newsSlice";
import { fetchCredibilityThunk } from "../../redux/credibility/credibilityActionThunks";
import { selectCredByUser } from "../../redux/credibility/credibilitySlice";
import { PUBLISHING_ELIGIBLE_TIERS } from "../../utils/credibilityTiers";

const NewsSubmitPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth || {});
  const credibility = useSelector((s) => selectCredByUser(s, user?.id));
  const { submitStatus, submitError, lastSubmitted } = useSelector((s) => s.news || {});

  const [title, setTitle] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [tickerInput, setTickerInput] = useState("");
  const [tickers, setTickers] = useState([]);

  useEffect(() => {
    if (user?.id) dispatch(fetchCredibilityThunk({ userId: user.id, soft: true }));
    return () => { dispatch(resetSubmitStatus()); };
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (submitStatus === "success" && lastSubmitted) {
      navigate("/news", { replace: true });
    }
  }, [submitStatus, lastSubmitted, navigate]);

  const canPublish = PUBLISHING_ELIGIBLE_TIERS.includes(credibility?.status_tier);

  const addTicker = () => {
    const sym = tickerInput.trim().toUpperCase();
    if (sym && !tickers.includes(sym)) setTickers([...tickers, sym]);
    setTickerInput("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !bodyContent.trim()) return;
    dispatch(submitArticleThunk({ title: title.trim(), bodyContent: bodyContent.trim(), tickerSymbols: tickers }));
  };

  // Hidden entirely for ineligible users -- not shown-and-disabled, per the
  // brief's own explicit instruction. credibility may still be loading on
  // first mount, so only redirect once we actually know the tier.
  if (credibility && !canPublish) {
    return (
      <div className="flex-1 bg-gray-900 text-gray-100 p-6 flex items-center justify-center h-screen text-center">
        <div>
          <Shield className="mx-auto text-gray-600 mb-4" size={40} />
          <p className="text-gray-400 mb-2">{t('news.submit.ineligibleMessage')}</p>
          <p className="text-gray-500 text-sm mb-6">{t('news.submit.currentTier', { tier: credibility.status_tier })}</p>
          <Link to="/news" className="text-violet-400 hover:text-violet-300">{t('news.backToPublishedNews')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-900 text-gray-100 p-6 pb-20">
      <div className="max-w-2xl mx-auto">
        <Link to="/news" className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition">
          <ArrowLeft size={18} /> {t('news.submit.back')}
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">{t('news.writeArticle')}</h1>
        <p className="text-gray-400 mb-8">
          {t('news.submit.pageSubtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('news.submit.titleLabel')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('news.submit.titlePlaceholder')}
              className="w-full bg-gray-800 text-gray-100 px-4 py-3 rounded-xl border border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('news.submit.bodyLabel')}</label>
            <textarea
              value={bodyContent}
              onChange={(e) => setBodyContent(e.target.value)}
              placeholder={t('news.submit.bodyPlaceholder')}
              rows={12}
              className="w-full bg-gray-800 text-gray-100 px-4 py-3 rounded-xl border border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('news.submit.relatedTickersLabel')}</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTicker(); } }}
                placeholder={t('news.submit.tickerPlaceholder')}
                className="flex-1 bg-gray-800 text-gray-100 px-4 py-2 rounded-xl border border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button type="button" onClick={addTicker} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm">
                {t('news.submit.add')}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tickers.map((t) => (
                <span key={t} className="bg-emerald-500/10 text-emerald-300 text-sm px-3 py-1 rounded-lg flex items-center gap-2">
                  {t}
                  <button type="button" onClick={() => setTickers(tickers.filter((x) => x !== t))}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {submitStatus === "error" && (
            <div className="bg-red-900/20 border border-red-800 text-red-300 text-sm px-4 py-3 rounded-xl">
              {submitError || t('news.submit.fallbackSubmitError')}
            </div>
          )}

          <button
            type="submit"
            disabled={submitStatus === "loading"}
            className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-medium py-3 rounded-xl transition"
          >
            {t('news.submit.submitForReview')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewsSubmitPage;
